import * as THREE from "three";
import ShapeGeometry from './ShapeGeometry.js';
import {doc} from '../docs.js';
import {lazy} from 'utils/lazy.js';

doc(
    "three.bufferGeometry",
    {
        vertices: "",
        itemSize: "",
        index: "",
    },
    "Create a BufferGeometry (to be passed to three.mesh)",
    () => 1,
    {
        tags: "Graphics",
        inlets: 3,
        connects: {
            object: "three.mesh",
        }
 
    }
);

export const threeBufferGeometry = (object, itemSize, index) => {
    const geometry = new THREE.BufferGeometry();
    let positionCreated = false;
    let indexComputed = false;

    return ([pts]) => {
        if (pts && pts.buffer) {
            if (positionCreated) {
                if (pts.length > geometry.attributes.position.array.length) {
                    geometry.attributes.position.array = pts;
                } else {
                    geometry.attributes.position.array.set(pts, 0);
                }
                indexComputed = false;
            } else {
                let array = new Float32Array(pts.length * 2);
                array.set(pts, 0);
                geometry.setAttribute(
                    'position',
                    new THREE.BufferAttribute( array, lazy(itemSize) || 3) );
                positionCreated = true;
            }
            geometry.attributes.position.needsUpdate = true;
        }

        if (lazy(index) && typeof lazy(index) !== "string" && !indexComputed) {
            if (geometry.index && geometry.index.array.length >= lazy(index).length) {
                geometry.index.array.set(lazy(index), 0);
            } else {
                // no index
                let indexArray = new Uint32Array(lazy(index).length*2);
                indexArray.set(lazy(index), 0);
                geometry.setIndex(new THREE.BufferAttribute(indexArray, 1)); 
            }
            geometry.index.needsUpdate = true;
            geometry.computeVertexNormals();
            geometry.attributes.normal.needsUpdate = true;
            indexComputed = true;
        }
        if (lazy(index)) {
            geometry.setDrawRange(0, lazy(index).length);
        } else {
            // console.log("NOT SETTING DRAW RANGE", pts, index, lazy(index), index());
        }
        if (indexComputed) {
            return [geometry];
        }
    };
};

doc(
    "three.circleGeometry",
    {
        bang: "Sends the geometry out through the outlet"
    },
    "Create a simple circle geometry (to be passed to mesh)",
    () => 1,
    {
        tags: "Graphics",
        connects: {
            object: "three.mesh",
        }
    }
);

const circleGeometry = (object) => {
    // 280
    let radius = (280/2)*.95;
    let geometry = new THREE.CircleGeometry(radius, 64);
    return ([_radius]) => {
        /*
        if (typeof radius === "string") {
            if (radius.startsWith("position")) {
                let tokens = radius.split(" ").filter(x => x !== "");
                tokens.splice(1).map(x => parseFloat(x));
            }
        }
        */
        return [geometry];
    };
};

doc(
    "three.planeGeometry",
    {
        width: "",
        height: ""
    },
    "Create a PlaneGeometry(to be passed to three.mesh)",
    () => 1,
    {
        tags: "Graphics",
        inlets: 2,
        connects: {
            object: "three.mesh",
        }
    }
);

const planeGeometry = (object, height) => {
    let geometry = new THREE.PlaneGeometry(300, 300); //window.innerWidth, window.innerHeight);
    return ([width]) => {
        object.geometry = geometry;
        return [geometry];
    };
};

doc(
    "three.renderTarget",
    {
        width: "",
        height: "",
    },
    "Set the renderTarget for a canvas",
    () => 2,
    {
        tags: "Graphics",
        inlets: 2,
        outlets: ["target", "texture"],
        connects: {
            object: "three.canvas",
            inlet: "renderTarget"
        }
    }
);
const renderTarget = (object, height) => {
    let target = new THREE.WebGLRenderTarget(
        300, /// 8, //resolution x
        300, // / 8, //resolution y
        {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBFormat
        }
    );
    return ([width]) => {
        if (width === "texture") {
            return [undefined, target.texture];
        }
        if (width === "bang") {
            return [target];
        }
        if (width && height() && (width !== target.width || height() !== target.height)) {
            target = new THREE.WebGLRenderTarget(
                width,
                height(),
                {
                    minFilter: THREE.LinearFilter,
                    magFilter: THREE.NearestFilter,
                    format: THREE.RGBFormat
                }
            );
        }
        return [target];
    };
};

doc(
    "three.torus",
    {
        x: "",
        y: "",
        z: ""
    },
    "Create a cube",
    () => 1,
    {
        tags: "Graphics",
        inlets: 3
    }
);

export const threeTorus= (object, y, z) => {
    const geometry = new THREE.TorusGeometry( 100, 25, 15, 30);
    var mat1 = new THREE.MeshBasicMaterial( { color: 0x555555 } );
    const cube = new THREE.Mesh( geometry, mat1 );

    return ([x]) => {
        return [cube];
    };
};

doc(
    "three.boxGeometry",
    {
    },
    "Create a simple box geometry (to be passed to mesh)",
    () => 1,
    {
        tags: "Graphics",
        connects: {
            object: "three.mesh",
        }
    }
);

const boxGeometry = (object, w, h, d, ws, hs, ds) => {
    // 280
    return ([ms]) => {
        let geometry = new THREE.BoxGeometry(
            w() || 100,
            h() || 100,
            d() || 100,
            ws() || 10,
            hs() || 10,
            ds() || 10);
        return [geometry];
    };
};

doc(
    "three.shapeGeometry",
    {
        "vertices": "List of points",
        "segmentLength" : "Number of segments"
        
    },
    "Create a simple box geometry (to be passed to mesh)",
    () => 1,
    {
        tags: "Graphics",
        inlets: 2,
        connects: {
            object: "three.mesh",
        }
    }
);

const shapeGeometry = (object, segmentsLength) => {
    // 280
    let geometry;
    return ([curvePts]) => {
        let vertices = [];
        for (let i=0; i < curvePts.length; i+= 2) {
            vertices.push(new THREE.Vector2(curvePts[i], curvePts[i+1]));
        }

        let _segmentsLength = segmentsLength() || 200;
        if (geometry) {
            geometry.updateShapes(new THREE.Shape(vertices), _segmentsLength);
        } else {
            geometry = new ShapeGeometry(new THREE.Shape(vertices), _segmentsLength);
        }
        return [geometry];
    };
};


export const geometry_3d = {
    "three.shapeGeometry": shapeGeometry,
    "three.circleGeometry": circleGeometry,
    "three.boxGeometry": boxGeometry,
    "three.torus": threeTorus,
    "three.renderTarget": renderTarget,
    "three.bufferGeometry": threeBufferGeometry,
    "three.planeGeometry": planeGeometry
};
